import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { useSalesOrdersSync, notifySalesOrdersChanged } from '../hooks/useSalesOrdersSync';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { Plus, LayoutGrid, List, ChevronRight, Trash2, X, RefreshCw, Calendar, FileText, AlertTriangle, CheckCircle2, CreditCard, Check, Download, ExternalLink, MessageCircle, Sparkles } from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import SubscribeButton from '../components/SubscribeButton';
import VoiceOrderModal from '../components/VoiceOrderModal';
import VoiceOrderButton from '../components/VoiceOrderButton';
import ProductCombobox from '../components/ProductCombobox';
import MobileModal from '../components/MobileModal';
import CodeLabel from '../components/CodeLabel';
import ProductCodeBadge from '../components/ProductCodeBadge';
import AddToPriceListButton from '../components/AddToPriceListButton';
import { useProductCodes } from '../context/ProductCodesContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const STAGE_CHIP = {
  'Pending':        'bg-zinc-800 text-zinc-300 border-zinc-700',
  'Planning':       'bg-blue-500/15 text-blue-300 border-blue-600/40',
  'In Production':  'bg-amber-500/15 text-amber-300 border-amber-600/40',
  'Completed':      'bg-green-500/15 text-green-400 border-green-600/40',
};

const downloadPdf = async (url, filename) => {
  try {
    const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename || 'document.pdf');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
  } catch (e) { toast.error('Failed to download PDF'); }
};

const STATUSES = ['Not Accepted', 'Accepted', 'In Production', 'Packing', 'Dispatched'];
const DELIVERY_STATUSES = ['Pending', 'In Transit', 'Out for Delivery', 'Delivered', 'Returned'];
const DELIVERY_STYLE = {
  'Pending':          'bg-zinc-900/60 border-zinc-700 text-zinc-400',
  'In Transit':       'bg-blue-900/30 border-blue-700 text-blue-300',
  'Out for Delivery': 'bg-amber-900/30 border-amber-700 text-amber-300',
  'Delivered':        'bg-green-900/30 border-green-700 text-green-300',
  'Returned':         'bg-red-900/30 border-red-700 text-red-300',
};

// Kanban 4-bucket view aligned with backend pipeline buckets
const PIPELINE_COLUMNS = [
  { key: 'pending',       label: 'Pending',        statuses: ['Not Accepted'],              next: 'Accepted',      dot: 'bg-red-400',    head: 'bg-red-900/20 border-red-700/40 text-red-300' },
  { key: 'planning',      label: 'Planning',       statuses: ['Accepted'],                   next: 'In Production', dot: 'bg-amber-400',  head: 'bg-amber-900/20 border-amber-700/40 text-amber-300' },
  { key: 'in_production', label: 'In Production',  statuses: ['In Production', 'Packing'],   next: 'Dispatched',    dot: 'bg-blue-400',   head: 'bg-blue-900/20 border-blue-700/40 text-blue-300' },
  { key: 'completed',     label: 'Completed',      statuses: ['Dispatched'],                 next: null,            dot: 'bg-green-400',  head: 'bg-green-900/20 border-green-700/40 text-green-300' },
];
const bucketOf = (status) => PIPELINE_COLUMNS.find(c => c.statuses.includes(status))?.key || 'pending';

const STATUS_STYLE = {
  'Not Accepted': 'bg-zinc-900/80 border-zinc-700 text-zinc-400',
  'Accepted':     'bg-blue-900/30 border-blue-700 text-blue-300',
  'In Production':'bg-amber-900/30 border-amber-700 text-amber-300',
  'Packing':      'bg-purple-900/30 border-purple-700 text-purple-300',
  'Dispatched':   'bg-green-900/30 border-green-700 text-green-300',
};

const EMPTY_ITEM = { product_id: '', product_name: '', product_code: '', qty: '', unit: 'kg', rate: '', amount: 0, hsn_code: '' };
const EMPTY_FORM = {
  client: '', client_gstin: '', client_address: '', client_phone: '', client_email: '', client_state: 'Maharashtra',
  items: [{ ...EMPTY_ITEM }],
  gst_enabled: true, gst_rate: 5,
  transport_exp: '',
  due_date: '', narration: '', notes: '',
  // Transport / Supply Chain
  dispatch_through: '', vehicle_no: '', lr_no: '',
  destination: '', buyer_ref: '', eway_bill_no: '',
  payment_terms: '',
  // Ship-to (consignee) — defaults to same as buyer
  ship_to_name: '', ship_to_address: '', ship_to_gstin: '',
  // Non-GST specific
  supplier_ref: '', other_ref: '', terms_of_delivery: '', dispatch_id: '', delivery_note_date: '',
  // P54/P55 pipeline fields
  production_due_date: '', dispatch_due_date: '',
  remark: '', priority: 'general', production_line: '',
};

export default function Sales() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [prodLines, setProdLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPriceList, setClientPriceList] = useState([]);
  // Inline payment shortcut
  const [payingOrder, setPayingOrder] = useState(null);
  const [quickPay, setQuickPay] = useState({ amount: '', method: 'NEFT', note: '' });
  const [quickPayLoading, setQuickPayLoading] = useState(false);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { lookupProduct } = useProductCodes();

  // Define fetchData before useAdminDelete to avoid TDZ error
  const fetchData = async () => {
    try {
      const [oRes, fgRes, rmRes, cRes, lRes] = await Promise.all([
        axios.get(`${API}/sales/orders`, { withCredentials: true }),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
        axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
        axios.get(`${API}/clients/`, { withCredentials: true }),
        axios.get(`${API}/production/lines`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setOrders(oRes.data);
      // Merge FG + RM so sales orders can sell either — each tagged with item_type
      const fgList = (fgRes.data || []).map(p => ({ ...p, item_type: 'finished' }));
      const rmList = (rmRes.data || []).map(p => ({
        ...p,
        item_type: 'raw',
        price_per_unit: p.current_rate || p.price_per_unit || 0,
      }));
      setProducts([...fgList, ...rmList]);
      setClients(cRes.data);
      setProdLines(lRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const [showVoiceOrder, setShowVoiceOrder] = useState(false);
  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(() => { fetchData(); notifySalesOrdersChanged(); });

  // Toggle per-item "ready to ship" tickmark — persists via backend, updates local state
  const toggleItemReady = async (orderId, itemIndex) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const cur = (order.items_ready || {})[String(itemIndex)];
    const next = !cur?.ready;
    try {
      const res = await axios.patch(`${API}/sales/orders/${orderId}/item-ready`, { item_index: itemIndex, ready: next }, { withCredentials: true });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, items_ready: res.data.items_ready } : o));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/sales/history`, { withCredentials: true });
      setHistoryOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { if (view === 'history') fetchHistory(); }, [view]);

  const handleQuickPay = async (orderId) => {
    if (!quickPay.amount || parseFloat(quickPay.amount) <= 0) return;
    setQuickPayLoading(true);
    try {
      await axios.post(`${API}/invoices/${orderId}/payment`, {
        amount: quickPay.amount,
        date: new Date().toISOString().slice(0, 10),
        method: quickPay.method,
        note: quickPay.note,
      }, { withCredentials: true });
      toast.success('Payment recorded!');
      setPayingOrder(null);
      setQuickPay({ amount: '', method: 'NEFT', note: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment error'); }
    finally { setQuickPayLoading(false); }
  }; // [{product_id, rate, discount_pct}]

  useEffect(() => { fetchData(); }, []);

  // Auto-open create modal when arriving with ?new=1 query param (from SalesPipeline)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      openModal();
      // Strip the param so back/refresh doesn't reopen
      const url = new URL(window.location.href);
      url.searchParams.delete('new');
      window.history.replaceState({}, '', url);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/sales/orders/${id}/status`, { status }, { withCredentials: true });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: res.data.status } : o));
      notifySalesOrdersChanged();
      // Instant in-app feedback so the operator sees confirmation of their
      // own action (the bell-icon notification is also auto-emitted server-
      // side so other watchers + push subscribers see the move too).
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating status'); }
  };

  // ── Sales-Order history actions (no invoice flow — that's the user intent:
  // keep sales orders separate from bills). Edit opens the order modal in edit
  // mode; WhatsApp/Email use free deep-links with the order summary.
  const openEdit = (o) => {
    setForm({
      id: o.id,
      client: o.client || '',
      items: (o.items && o.items.length) ? o.items.map(it => ({
        product_id: it.product_id || '',
        product_name: it.product_name || '',
        qty: it.qty || it.quantity || 1,
        rate: it.rate || 0,
        unit: it.unit || 'kg',
      })) : [{ product_id: '', qty: 1, rate: 0, unit: 'kg' }],
      delivery_date: o.delivery_date || o.production_due_date || '',
      dispatch_due_date: o.dispatch_due_date || '',
      priority: o.priority || 'general',
      production_line: o.production_line || '',
      remark: o.remark || o.narration || '',
      gst_enabled: o.gst_enabled ?? true,
      editingId: o.id,
    });
    setShowModal(true);
  };

  const shareOrderWhatsApp = (o) => {
    const lines = [
      `*Sales Order ${o.so_number || o.voucher_no || ''}*`,
      `Client: ${o.client}`,
      ...(o.items || []).slice(0, 6).map(it => `• ${it.product_name || it.name} — ${it.qty || it.quantity} ${it.unit || ''}`),
      `Total: *${fmt(o.total_amount || 0)}*`,
      `Stage: ${o.stage || o.status || '—'}`,
      `Dispatch by: ${o.dispatch_due_date || o.delivery_date || '—'}`,
    ].filter(Boolean);
    const msg = lines.join('\n') + '\n\n_— Tirth Industries_';
    // Look up client phone if available (best-effort)
    let phone = (o.client_phone || '').replace(/\D/g, '');
    if (phone && phone.length === 10) phone = '91' + phone;
    const target = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    window.open(`${target}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareOrderEmail = (o) => {
    const subject = `Sales Order ${o.so_number || o.voucher_no || ''} · ${o.client}`;
    const bodyLines = [
      `Sales Order: ${o.so_number || o.voucher_no || ''}`,
      `Client: ${o.client}`,
      '',
      'Items:',
      ...(o.items || []).map(it => `  - ${it.product_name || it.name} × ${it.qty || it.quantity} ${it.unit || ''} @ ₹${it.rate || 0}`),
      '',
      `Total: ₹${(o.total_amount || 0).toLocaleString('en-IN')}`,
      `Stage: ${o.stage || o.status || '—'}`,
      `Dispatch by: ${o.dispatch_due_date || o.delivery_date || '—'}`,
      '',
      'Note: This is an internal sales order — an invoice will be raised separately at time of dispatch.',
      '— Tirth Industries',
    ];
    const body = bodyLines.join('\n');
    const toEmail = o.client_email || '';
    window.open(`mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  // Delivery status + tracking link update (separate from order status)
  const updateDelivery = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/sales/orders/${id}/delivery`, patch, { withCredentials: true });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...res.data } : o));
      notifySalesOrdersChanged();
      if (patch.delivery_status) toast.success(`Delivery → ${patch.delivery_status}. Customer notified in portal.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating delivery'); }
  };

  // WhatsApp share — opens wa.me in a new tab, pre-filled with a short message
  // including the tracking link. Uses the client's phone when available,
  // otherwise opens the universal chooser.
  const shareViaWhatsApp = (o) => {
    const voucher = o.invoice_number || o.voucher_no || o.id;
    const status = o.delivery_status || 'Dispatched';
    const client = o.client || 'Customer';
    const trackLine = o.tracking_link ? `\nTrack it here: ${o.tracking_link}` : '';
    const msg = `Hi ${client}, your order ${voucher} is now *${status}*.${trackLine}\n\n— Tirth Industries`;
    const phone = (o.client_phone || '').replace(/[^0-9]/g, '');
    const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  // deleteOrder now uses useAdminDelete hook via requestDelete (see button onClick handlers)

  const openModal = () => {
    setForm({ ...EMPTY_FORM, items: [{ ...EMPTY_ITEM }] });
    setSelectedClient(null);
    setClientPriceList([]);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setSelectedClient(null); setClientPriceList([]); };

  // ── Line item helpers ──────────────────────────────────────────────────
  const updateItem = (idx, field, value) => {
    setForm(f => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [field]: value };
        if (field === 'qty' || field === 'rate') {
          const q = parseFloat(field === 'qty' ? value : it.qty) || 0;
          const r = parseFloat(field === 'rate' ? value : it.rate) || 0;
          updated.amount = Math.round(q * r * 100) / 100;
        }
        return updated;
      });
      return { ...f, items };
    });
  };

  const setProductForItem = (idx, productId) => {
    const p = products.find(pr => pr.id === productId);
    const productCode = (p && (p.product_code || lookupProduct(p.name))) || '';
    // Check if client has a custom price for this product
    const priceEntry = clientPriceList.find(pl => pl.product_id === productId);
    setForm(f => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        let rate = p?.price_per_unit || 0;
        if (priceEntry && priceEntry.rate > 0) {
          rate = priceEntry.rate;
          // Apply discount if set
          if (priceEntry.discount_pct > 0) {
            rate = rate * (1 - priceEntry.discount_pct / 100);
          }
          // Apply markup if set
          if (priceEntry.markup_pct > 0) {
            rate = rate * (1 + priceEntry.markup_pct / 100);
          }
          rate = Math.round(rate * 100) / 100;
        }
        const unit = priceEntry?.unit || p?.unit || it.unit;
        const q = parseFloat(it.qty) || 0;
        return { ...it, product_id: productId, product_name: p?.name || '', product_code: productCode, item_type: p?.item_type || 'finished', rate: String(rate), unit, amount: q * rate, has_custom_price: !!priceEntry };
      });
      return { ...f, items, gst_rate: p?.gst_rate || f.gst_rate };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // ── Totals ────────────────────────────────────────────────────────────
  const subtotal = form.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const gstAmt   = form.gst_enabled ? Math.round(subtotal * form.gst_rate / 100 * 100) / 100 : 0;
  const cgst     = Math.round(gstAmt / 2 * 100) / 100;
  const sgst     = cgst;
  const transport = parseFloat(form.transport_exp) || 0;
  const grandTotal = subtotal + gstAmt + transport;

  // ── Credit limit ──────────────────────────────────────────────────────
  const getClientOutstanding = (name) =>
    orders.filter(o => o.client === name && o.payment_status !== 'Paid')
      .reduce((s, o) => s + (parseFloat(o.total_amount || 0) - parseFloat(o.paid_amount || 0)), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter(it => it.product_name || it.qty),
        gst_rate: form.gst_enabled ? form.gst_rate : 0,
        transport_exp: parseFloat(form.transport_exp) || 0,
        client_state: form.client_state || '',
        terms_of_delivery: form.terms_of_delivery || '',
        supplier_ref: form.supplier_ref || '',
        other_ref: form.other_ref || '',
        dispatch_id: form.dispatch_id || '',
        delivery_note_date: form.delivery_note_date || '',
      };
      // Edit mode — hit PUT to update the existing order (set in openEdit()).
      let res;
      if (form.editingId) {
        res = await axios.put(`${API}/sales/orders/${form.editingId}`, payload, { withCredentials: true });
        setOrders(prev => prev.map(o => o.id === form.editingId ? res.data : o));
        toast.success('Sales order updated');
      } else {
        res = await axios.post(`${API}/sales/orders`, payload, { withCredentials: true });
        setOrders(prev => [res.data, ...prev]);
        toast.success(`${form.gst_enabled ? 'GST' : 'Non-GST'} invoice created!`);
      }
      notifySalesOrdersChanged();
      closeModal();
      if (view === 'history') fetchHistory();
    } catch (e) {
      console.error('Create order error:', e, e.response?.data);
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : (e.message || 'Error creating order');
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };

  const handleAddNewDebtor = async () => {
    if (!newDebtorForm.name.trim()) return;
    try {
      const res = await axios.post(`${API}/clients/`, newDebtorForm, { withCredentials: true });
      setClients(prev => [...prev, res.data]);
      // Auto-select the new debtor
      const c = res.data;
      setForm(f => ({ ...f, client: c.name, client_gstin: c.gstin || '', client_address: c.address || '', client_phone: c.phone || '', client_email: c.email || '', client_state: c.state || 'Maharashtra' }));
      setSelectedClient(c);
      setShowAddDebtor(false);
      setNewDebtorForm({ name: '', type: 'Debtor', address: '', city: '', state: 'Maharashtra', phone: '', gstin: '', email: '', payment_terms: 'Net 30' });
      toast.success('Debtor added & selected!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding debtor'); }
  };

  const filteredOrders = (() => {
    if (filterStatus === 'All') return orders;
    const col = PIPELINE_COLUMNS.find(c => c.key === filterStatus);
    if (col) return orders.filter(o => col.statuses.includes(o.status));
    return orders.filter(o => o.status === filterStatus);
  })();
  const totalRevenue = orders.filter(o => o.status === 'Dispatched').reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading sales...</div>;

  const nextStatus = (current) => STATUSES[Math.min(STATUSES.indexOf(current) + 1, STATUSES.length - 1)];

  return (
    <div className="space-y-5" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Sales Orders</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{orders.length} orders · {fmt(totalRevenue)} dispatched</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <button data-testid="kanban-view-btn" onClick={() => setView('kanban')} className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${view === 'kanban' ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}><LayoutGrid size={13} />Kanban</button>
            <button data-testid="table-view-btn" onClick={() => setView('table')} className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${view === 'table' ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}><List size={13} />Table</button>
            <button data-testid="history-view-btn" onClick={() => setView('history')} className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${view === 'history' ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}><Calendar size={13} />History</button>
            <button data-testid="calendar-view-btn" onClick={() => setView('calendar')} className={`flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${view === 'calendar' ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}><Calendar size={13} />Calendar</button>
          </div>
          <button
            onClick={() => setShowVoiceOrder(true)}
            data-testid="voice-order-btn"
            title="Dictate a sales order"
            className="flex items-center gap-1.5 bg-purple-500/15 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 text-xs px-3 py-2 rounded-xl transition-colors"
          >
            <Sparkles size={14} /> Voice Order
          </button>
          <button data-testid="add-order-btn" onClick={openModal} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> New Order
          </button>
        </div>
      </div>

      {/* Status summary — 4 pipeline stages (same as Sales Pipeline) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PIPELINE_COLUMNS.map(col => {
          const count = orders.filter(o => col.statuses.includes(o.status)).length;
          const active = filterStatus === col.key;
          return (
            <button key={col.key} onClick={() => setFilterStatus(active ? 'All' : col.key)}
              data-testid={`stat-${col.key}`}
              className={`p-3 rounded-xl border text-center transition-all ${col.head} ${active ? 'ring-2 ring-amber-500' : ''}`}>
              <div className="text-lg font-heading font-bold">{count}</div>
              <div className="text-xs mt-0.5 opacity-80">{col.label}</div>
            </button>
          );
        })}
      </div>

      {/* Kanban — 4 pipeline columns */}
      {view === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {PIPELINE_COLUMNS.map(col => {
            const colOrders = orders.filter(o => col.statuses.includes(o.status));
            return (
              <div key={col.key} className={`flex-shrink-0 w-64 rounded-xl border flex flex-col ${col.head.replace('text-', 'border-').split(' ')[1]} bg-zinc-950`} data-testid={`kanban-col-${col.key}`}>
                <div className={`px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between ${col.head}`}>
                  <span className="text-xs font-semibold flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${col.dot}`} /> {col.label}
                  </span>
                  <span className="text-xs bg-black/30 px-1.5 py-0.5 rounded-full font-mono">{colOrders.length}</span>
                </div>
                <div className="p-2 space-y-2 flex-1 min-h-20">
                  {colOrders.map(order => (
                    <div key={order.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-zinc-700 transition-all" data-testid={`order-card-${order.id}`}>
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <div className="text-zinc-50 text-xs font-semibold leading-tight">{order.client}</div>
                          {(order.so_number || order.invoice_number || order.voucher_no) && <div className="text-amber-400/60 text-[10px] font-mono mt-0.5" data-testid={`so-no-${order.id}`}>{order.so_number || order.invoice_number || order.voucher_no}</div>}
                        </div>
                        <div className="flex items-center gap-1">
                          <SubscribeButton refType="sales_order" refId={order.id} label={order.voucher_no || order.invoice_number || order.client} size="xs" />
                          <button onClick={() => setInvoiceOrder(order)} className="text-zinc-600 hover:text-amber-400 transition-colors" title="View"><FileText size={11} /></button>
                        </div>
                      </div>
                      <div className="text-zinc-400 text-xs">
                        {(order.items?.length || 0) > 0 && !order.items?.length && (
                          <span className="inline-flex items-center gap-1.5">
                            <ProductCodeBadge name={order.product_name} code={order.product_code} />
                            <CodeLabel name={order.product_name} fallback="—" />
                          </span>
                        )}
                        {/* Per-item ready counter — restaurant kanban style */}
                        {(() => {
                          const total = order.items?.length || 0;
                          if (total === 0) return null;
                          const done = Object.keys(order.items_ready || {}).filter(k => order.items_ready[k]?.ready).length;
                          const all = done === total;
                          return (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${all ? 'bg-green-500/20 text-green-400 border-green-700' : done > 0 ? 'bg-amber-500/20 text-amber-400 border-amber-700' : 'bg-zinc-800 text-zinc-500 border-zinc-700'}`} data-testid={`ready-chip-${order.id}`}>
                              {all ? `✓ Ready` : `${done}/${total} ready`}
                            </span>
                          );
                        })()}
                      </div>
                      {/* Per-item checkbox list — restaurant-style, outside the invoice view */}
                      {order.items?.length > 0 && (
                        <div className="mt-1.5 space-y-0.5 bg-zinc-950/50 rounded-md p-1.5 border border-zinc-800/60">
                          {order.items.map((it, idx) => {
                            const ready = !!(order.items_ready || {})[String(idx)]?.ready;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleItemReady(order.id, idx); }}
                                data-testid={`kanban-tick-${order.id}-${idx}`}
                                className={`w-full text-left flex items-start gap-1.5 px-1 py-0.5 rounded hover:bg-zinc-800/60 transition-colors ${ready ? 'opacity-60' : ''}`}
                                title={ready ? 'Click to unmark' : 'Click to mark ready'}>
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${ready ? 'bg-green-500 border-green-400' : 'bg-zinc-900 border-zinc-600'}`}>
                                  {ready && <CheckCircle2 size={9} className="text-black" strokeWidth={3} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className={`text-[10px] ${ready ? 'line-through text-zinc-500' : 'text-zinc-300'}`}>
                                    {it.product_name || it.name || '—'}
                                  </span>
                                  <span className="text-zinc-600 text-[9px] ml-1">× {it.qty || it.quantity || 0} {it.unit || ''}</span>
                                  {it.item_type === 'raw' && <span className="ml-1 text-[8px] uppercase text-emerald-400">RM</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* Pipeline chips: priority · prod line · dispatch due */}
                      {(order.priority || order.production_line || order.dispatch_due_date) && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`chips-${order.id}`}>
                          {order.priority && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                              order.priority === 'high' ? 'bg-red-500/20 text-red-300'
                              : order.priority === 'low' ? 'bg-zinc-800 text-zinc-400'
                              : 'bg-blue-500/15 text-blue-300'}`}>
                              {order.priority === 'high' ? 'High' : order.priority === 'low' ? 'Low' : 'General'}
                            </span>
                          )}
                          {order.production_line && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-semibold">
                              {order.production_line}
                            </span>
                          )}
                          {order.dispatch_due_date && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 font-mono">
                              ⎋ {order.dispatch_due_date}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-amber-400 text-xs font-mono font-semibold">{fmt(order.total_amount)}</span>
                        {order.payment_status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${order.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700' : order.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-red-900/30 text-red-400 border-red-800'}`}>{order.payment_status}</span>
                        )}
                      </div>
                      {order.due_date && <div className="text-zinc-600 text-xs mt-1 flex items-center gap-1"><Calendar size={10} />{order.due_date?.slice(0, 10)}</div>}
                      {/* Inline Quick Pay Form */}
                      {payingOrder === order.id && (
                        <div className="mt-2 p-2 bg-zinc-800 rounded-lg border border-zinc-700 space-y-1.5" data-testid={`quick-pay-form-${order.id}`}>
                          <div className="flex gap-1">
                            <input type="number" value={quickPay.amount} onChange={e => setQuickPay(f => ({ ...f, amount: e.target.value }))} placeholder="Amount" className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" data-testid={`quick-pay-amount-${order.id}`} />
                            <select value={quickPay.method} onChange={e => setQuickPay(f => ({ ...f, method: e.target.value }))} className="bg-zinc-900 border border-zinc-700 rounded px-1 py-1 text-zinc-50 text-xs">
                              {['NEFT','RTGS','IMPS','UPI','Cash','Cheque'].map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
                            </select>
                          </div>
                          <input value={quickPay.note} onChange={e => setQuickPay(f => ({ ...f, note: e.target.value }))} placeholder="UTR / Ref" className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                          <div className="flex gap-1">
                            <button onClick={() => handleQuickPay(order.id)} disabled={quickPayLoading || !quickPay.amount} className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-xs py-1 rounded flex items-center justify-center gap-1" data-testid={`quick-pay-submit-${order.id}`}><Check size={10} /> {quickPayLoading ? '...' : 'Pay'}</button>
                            <button onClick={() => setPayingOrder(null)} className="px-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs py-1 rounded"><X size={10} /></button>
                          </div>
                        </div>
                      )}
                      {col.next && (
                        <button onClick={() => updateStatus(order.id, col.next)} className="mt-2 w-full text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg py-1.5 flex items-center justify-center gap-1 transition-colors">
                          Move to {col.next} <ChevronRight size={11} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      {view === 'table' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1280px]" data-testid="sales-orders-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  {['Invoice No', 'Type', 'Client', 'Items', 'Total', 'Due Date', 'Status', 'Delivery', 'Tracking', 'Payment', ''].map(h => (
                    <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(o => (
                  <tr key={o.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold whitespace-nowrap">{o.so_number || o.invoice_number || o.voucher_no || o.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${o.invoice_type === 'GST' || o.gst_enabled ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                        {o.invoice_type || (o.gst_enabled ? 'GST' : 'Non-GST')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-xs font-medium whitespace-nowrap max-w-[220px] truncate" title={o.client}>{o.client}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap max-w-[240px] truncate" title={o.product_name}>
                      {o.items?.length > 1 ? `${o.items.length} items` : (
                        <span className="inline-flex items-center gap-1.5">
                          <ProductCodeBadge name={o.product_name} code={o.product_code} />
                          <CodeLabel name={o.product_name} fallback="—" />
                        </span>
                      )}
                      {(() => {
                        const total = o.items?.length || 0;
                        if (total === 0) return null;
                        const done = Object.keys(o.items_ready || {}).filter(k => o.items_ready[k]?.ready).length;
                        if (done === 0) return null;
                        const all = done === total;
                        return (
                          <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border font-semibold ${all ? 'bg-green-500/20 text-green-400 border-green-700' : 'bg-amber-500/20 text-amber-400 border-amber-700'}`}>
                            {all ? '✓' : `${done}/${total}`}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold whitespace-nowrap">{fmt(o.total_amount)}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">{o.due_date?.slice(0, 10)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-lg border bg-transparent cursor-pointer min-w-[120px] ${STATUS_STYLE[o.status]}`} data-testid={`status-select-${o.id}`}>
                        {STATUSES.map(s => <option key={s} value={s} className="bg-zinc-900 text-zinc-50">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select value={o.delivery_status || 'Pending'} onChange={e => updateDelivery(o.id, { delivery_status: e.target.value })}
                        data-testid={`delivery-select-${o.id}`}
                        className={`text-xs px-2 py-1 rounded-lg border bg-transparent cursor-pointer min-w-[140px] ${DELIVERY_STYLE[o.delivery_status || 'Pending']}`}>
                        {DELIVERY_STATUSES.map(s => <option key={s} value={s} className="bg-zinc-900 text-zinc-50">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="url"
                          defaultValue={o.tracking_link || ''}
                          onBlur={e => { if (e.target.value !== (o.tracking_link || '')) updateDelivery(o.id, { tracking_link: e.target.value }); }}
                          placeholder="https://…"
                          data-testid={`tracking-input-${o.id}`}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-zinc-50 text-[10px] w-36 min-w-[144px] focus:outline-none focus:border-amber-500"
                        />
                        {o.tracking_link && (
                          <>
                            <a href={o.tracking_link} target="_blank" rel="noreferrer" title="Open tracking link"
                              data-testid={`tracking-open-${o.id}`}
                              className="text-sky-400 hover:text-sky-300"><ExternalLink size={12} /></a>
                            <button
                              type="button"
                              onClick={() => shareViaWhatsApp(o)}
                              title="Share tracking link via WhatsApp"
                              data-testid={`wa-share-${o.id}`}
                              className="text-green-500 hover:text-green-400">
                              <MessageCircle size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {o.payment_status && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full border whitespace-nowrap ${o.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700' : o.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-red-900/30 text-red-400 border-red-800'}`}>{o.payment_status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => { setPayingOrder(payingOrder === o.id ? null : o.id); setQuickPay({ amount: '', method: 'NEFT', note: '' }); }} className={`transition-colors ${payingOrder === o.id ? 'text-amber-400' : 'text-zinc-500 hover:text-green-400'}`} title="Quick Payment" data-testid={`table-pay-btn-${o.id}`}><CreditCard size={13} /></button>
                        <button onClick={() => downloadPdf(`${API}/invoices/${o.id}/pdf`, `Invoice_${o.voucher_no || o.id}.pdf`)} className="text-zinc-500 hover:text-blue-400 transition-colors" title="Download PDF" data-testid={`table-pdf-btn-${o.id}`}><Download size={13} /></button>
                        <button onClick={() => setInvoiceOrder(o)} className="text-zinc-500 hover:text-amber-400 transition-colors" title="Invoice" data-testid={`table-invoice-btn-${o.id}`}><FileText size={13} /></button>
                        {isAdmin && <button onClick={() => requestDelete(`/sales/orders/${o.id}`, o.invoice_number || o.voucher_no || o.client, `Delete order for ${o.client}. Stock restored, dispatches removed.`)} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={13} /></button>}
                      </div>
                      {/* Inline Quick Pay for Table Row */}
                      {payingOrder === o.id && (
                        <div className="mt-1.5 flex items-center gap-1 whitespace-nowrap" data-testid={`table-quick-pay-form-${o.id}`}>
                          <input type="number" value={quickPay.amount} onChange={e => setQuickPay(f => ({ ...f, amount: e.target.value }))} placeholder="₹ Amt" className="w-20 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" data-testid={`table-pay-amount-${o.id}`} />
                          <select value={quickPay.method} onChange={e => setQuickPay(f => ({ ...f, method: e.target.value }))} className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-zinc-50 text-xs min-w-[72px]">
                            {['NEFT','RTGS','UPI','Cash','Cheque'].map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
                          </select>
                          <button onClick={() => handleQuickPay(o.id)} disabled={quickPayLoading || !quickPay.amount} className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-xs px-2 py-0.5 rounded" data-testid={`table-pay-submit-${o.id}`}><Check size={10} /></button>
                          <button onClick={() => setPayingOrder(null)} className="text-zinc-500 hover:text-zinc-300"><X size={10} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredOrders.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No orders found</div>}
        </div>
      )}

      {/* History */}
      {view === 'history' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <span className="text-zinc-300 text-xs font-semibold">Sales Order History</span>
              <span className="text-zinc-500 text-[10px] ml-2">Completed orders auto-hide after 7 days · No invoice here (use Bill Log for invoices)</span>
            </div>
            <span className="text-zinc-500 text-[10px] font-mono">{historyOrders.length} orders</span>
          </div>
          {historyLoading ? <div className="text-zinc-400 text-sm animate-pulse p-8 text-center">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]" data-testid="sales-history-table">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {['SO No', 'Client', 'Items', 'Total', 'Stage', 'Updated', 'Actions'].map(h => (
                      <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historyOrders.map(o => (
                    <tr key={o.id} data-testid={`history-row-${o.id}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="px-4 py-3 font-mono text-amber-400 text-xs whitespace-nowrap">{o.so_number || o.voucher_no || '—'}</td>
                      <td className="px-4 py-3 text-zinc-200 text-xs whitespace-nowrap max-w-[220px] truncate" title={o.client}>{o.client}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap max-w-[240px] truncate" title={o.product_name}>
                        {o.items?.length > 1 ? `${o.items.length} items` : (
                          <span className="inline-flex items-center gap-1.5">
                            <ProductCodeBadge name={o.product_name} code={o.product_code} />
                            <CodeLabel name={o.product_name} fallback="—" />
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-amber-400 text-xs whitespace-nowrap">{fmt(o.total_amount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${STAGE_CHIP[o.stage] || STAGE_CHIP['Pending']}`}
                          data-testid={`history-stage-${o.id}`}>
                          {o.stage || 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs font-mono whitespace-nowrap">
                        {(o.dispatched_at || o.updated_at || o.created_at || '').slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {/* View/Edit — open the existing edit modal (no invoice flow) */}
                          <button onClick={() => openEdit(o)}
                            data-testid={`history-edit-${o.id}`}
                            title="View / Edit Sales Order"
                            className="p-1.5 rounded bg-zinc-800 text-amber-400 hover:bg-amber-900/30 transition-colors">
                            <FileText size={11} />
                          </button>
                          {/* WhatsApp — deep-link with the order summary (no invoice attached) */}
                          <button onClick={() => shareOrderWhatsApp(o)}
                            data-testid={`history-whatsapp-${o.id}`}
                            title="Share on WhatsApp"
                            className="p-1.5 rounded bg-zinc-800 text-green-400 hover:bg-green-900/30 transition-colors">
                            <MessageCircle size={11} />
                          </button>
                          {/* Email — pre-filled mailto with order summary */}
                          <button onClick={() => shareOrderEmail(o)}
                            data-testid={`history-email-${o.id}`}
                            title="Email Order"
                            className="p-1.5 rounded bg-zinc-800 text-blue-400 hover:bg-blue-900/30 transition-colors">
                            <ExternalLink size={11} />
                          </button>
                          {/* Delete — admin/manager only */}
                          {(user?.role === 'admin' || user?.role === 'owner' || user?.role === 'manager') && (
                            <button onClick={() => requestDelete({
                              label: o.so_number || o.voucher_no || 'this order',
                              message: `Delete sales order ${o.so_number || o.voucher_no}? This does NOT affect any linked bill.`,
                              onConfirm: async () => {
                                await axios.delete(`${API}/sales/orders/${o.id}`, { withCredentials: true });
                              },
                            })}
                              data-testid={`history-delete-${o.id}`}
                              title="Delete Sales Order"
                              className="p-1.5 rounded bg-zinc-800 text-red-400 hover:bg-red-900/30 transition-colors">
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!historyLoading && historyOrders.length === 0 && (
            <div className="text-center py-12 text-zinc-600 text-xs">No sales orders in the last 60 days</div>
          )}
        </div>
      )}


      {/* ══ SALES CALENDAR ══ */}
      {view === 'calendar' && (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(i);

        const getOrdersForDay = (day) => {
          if (!day) return [];
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          return orders.filter(o => o.created_at?.startsWith(dateStr) || o.due_date === dateStr);
        };

        return (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="sales-calendar">
            <div className="px-4 py-3 border-b border-zinc-800 text-center">
              <h3 className="text-zinc-200 text-sm font-semibold">{now.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
            </div>
            <div className="grid grid-cols-7 gap-px bg-zinc-800">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="bg-zinc-900 text-center text-zinc-500 text-[10px] py-2 font-medium">{d}</div>
              ))}
              {days.map((day, i) => {
                const dayOrders = getOrdersForDay(day);
                const isToday = day === now.getDate();
                return (
                  <div key={i} className={`bg-zinc-900 min-h-16 p-1 ${!day ? 'opacity-30' : ''} ${isToday ? 'ring-1 ring-amber-500/50' : ''}`}>
                    {day && <div className={`text-[10px] font-mono ${isToday ? 'text-amber-400 font-bold' : 'text-zinc-500'}`}>{day}</div>}
                    {dayOrders.slice(0, 3).map(o => (
                      <div key={o.id} className={`text-[8px] px-1 py-0.5 rounded mt-0.5 truncate ${o.status === 'Dispatched' ? 'bg-green-900/30 text-green-400' : 'bg-amber-900/20 text-amber-400'}`}>
                        {o.client?.split(' ')[0]}
                      </div>
                    ))}
                    {dayOrders.length > 3 && <div className="text-[8px] text-zinc-600">+{dayOrders.length - 3}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}


      {/* ── Simple Sales Order Modal ── */}
      {showModal && (
        <MobileModal onClose={closeModal} maxWidth="md" testId="sales-order-modal" className="max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="font-heading font-semibold text-zinc-50">New Sales Order</h3>
                <VoiceOrderButton onDraft={(draft) => {
                  // Fuzzy-match client name into known clients if possible
                  const cn = (draft.client_name || '').toLowerCase();
                  const matched = clients.find(c => c.name.toLowerCase() === cn)
                    || clients.find(c => c.name.toLowerCase().includes(cn) && cn.length > 2)
                    || null;
                  const items = (draft.items || []).map(it => {
                    const prod = products.find(p => p.name.toLowerCase() === (it.product || '').toLowerCase())
                      || products.find(p => p.name.toLowerCase().includes((it.product || '').toLowerCase()) && (it.product || '').length > 2);
                    return {
                      product_name: prod ? prod.name : (it.product || ''),
                      qty: it.qty || '',
                      rate: prod?.price || '',
                      unit: prod?.unit || it.unit || 'kg',
                    };
                  });
                  setForm(f => ({
                    ...f,
                    client: matched?.name || draft.client_name || f.client,
                    client_gstin: matched?.gstin || f.client_gstin,
                    client_address: matched ? [matched.address, matched.city, matched.state].filter(Boolean).join(', ') : f.client_address,
                    client_phone: matched?.phone || f.client_phone,
                    client_state: matched?.state || f.client_state,
                    gst_enabled: draft.gst_enabled !== undefined ? draft.gst_enabled : f.gst_enabled,
                    items: items.length > 0 ? items : f.items,
                    notes: draft.notes || f.notes,
                  }));
                }} />
              </div>
              <button onClick={closeModal}><X size={16} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} data-testid="order-form" className="p-5 space-y-3 overflow-y-auto flex-1">
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm(f => ({ ...f, gst_enabled: true }))}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.gst_enabled ? 'bg-green-900/30 border-green-600 text-green-300' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
                  GST (Tirth Industries)
                </button>
                <button type="button" onClick={() => setForm(f => ({ ...f, gst_enabled: false }))}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${!form.gst_enabled ? 'bg-amber-900/30 border-amber-600 text-amber-300' : 'bg-zinc-800 border-zinc-700 text-zinc-500'}`}>
                  Non-GST (Enterprises)
                </button>
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Client *</label>
                <select value={selectedClient?.id || ''} data-testid="form-client-select"
                  onChange={async e => {
                    const c = clients.find(cl => cl.id === e.target.value);
                    if (!c) { setSelectedClient(null); setClientPriceList([]); return; }
                    setSelectedClient(c);
                    setForm(f => ({ ...f, client: c.name, client_gstin: c.gstin || '', client_address: [c.address, c.city, c.state].filter(Boolean).join(', '), client_phone: c.phone || '', client_state: c.state || 'Maharashtra' }));
                    try {
                      const pl = await axios.get(`${API}/clients/${c.id}/price-list`, { withCredentials: true });
                      setClientPriceList(pl.data || []);
                    } catch { setClientPriceList([]); }
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none mb-1">
                  <option value="">— Select Debtor —</option>
                  {clients.filter(c => (c.type === 'Debtor' || c.type === 'Both') && (form.gst_enabled ? !!(c.gstin && c.gstin.trim()) : !(c.gstin && c.gstin.trim()))).map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` (${c.gstin})` : ''}</option>)}
                </select>
                <input value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} required placeholder="Or type client name" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none" />
                {selectedClient && clientPriceList.length > 0 && (
                  <div className="text-[10px] text-green-400 mt-1 flex items-center gap-1" data-testid="price-list-indicator">
                    ⚡ {clientPriceList.length} custom rate(s) loaded for this client — rates will auto-fill from the price list
                  </div>
                )}
              </div>
              <div>
                <label className="text-zinc-500 text-xs mb-1 block">Items <span className="text-zinc-600">(rate auto-fills from client price list → product default)</span></label>
                {form.items.map((it, i) => {
                  const priceEntry = clientPriceList.find(pl => pl.product_id === it.product_id);
                  const rateSource = it.product_id
                    ? (priceEntry && priceEntry.rate > 0 ? 'client' : (products.find(p => p.id === it.product_id)?.price_per_unit > 0 ? 'default' : 'none'))
                    : '';
                  return (
                  <div key={i} className="flex items-center gap-1 mb-1" data-testid={`item-row-${i}`}>
                    <ProductCombobox
                      products={products}
                      value={it.product_id || ''}
                      onChange={(pid) => setProductForItem(i, pid)}
                      groupByType
                      placeholder="Type product…"
                      testId={`item-product-${i}`}
                      className="flex-1"
                    />
                    <input type="number" min="0" placeholder="Qty" value={it.qty || ''} onChange={e => updateItem(i, 'qty', e.target.value)}
                      className="w-16 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-200 text-xs focus:outline-none" />
                    <input type="number" min="0" placeholder="Rate" value={it.rate || ''} onChange={e => updateItem(i, 'rate', e.target.value)}
                      className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-200 text-xs focus:outline-none" />
                    {rateSource === 'client' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40 font-semibold whitespace-nowrap" title="Rate from client price list">
                        CLIENT
                      </span>
                    )}
                    {rateSource === 'default' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 font-semibold whitespace-nowrap" title="Rate from product default">
                        DEFAULT
                      </span>
                    )}
                    {rateSource === 'none' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40 font-semibold whitespace-nowrap" title="No rate configured — enter manually">
                        NO RATE
                      </span>
                    )}
                    {/* Stock availability badge — tells user whether to ship from stock or trigger production */}
                    {it.product_id && (() => {
                      const prod = products.find(p => p.id === it.product_id);
                      if (!prod) return null;
                      const qty = parseFloat(it.qty) || 0;
                      const stock = parseFloat(prod.current_stock) || 0;
                      if (qty <= 0) return null;
                      if (stock >= qty) {
                        return <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40 font-semibold whitespace-nowrap" title={`In stock: ${stock} ${prod.unit || ''}`}>✓ IN STOCK</span>;
                      }
                      const short = qty - stock;
                      const label = prod.item_type === 'raw' ? 'NEEDS RM PO' : 'NEEDS PRODUCTION';
                      return <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-semibold whitespace-nowrap" title={`Short by ${short.toFixed(1)} ${prod.unit || ''} (stock ${stock})`}>⚠ {label} · {short.toFixed(0)} {prod.unit || ''}</span>;
                    })()}
                    {/* iter202 — one-click 'Add to price list' for the picked
                        product (FG or RM). Self-hides once an entry exists for
                        this client/product pair. */}
                    {selectedClient && it.product_id && !it.has_custom_price && (
                      <AddToPriceListButton
                        clientId={selectedClient.id}
                        product={{
                          product_id: it.product_id,
                          product_name: it.product_name,
                          item_type: it.item_type || 'finished',
                          unit: it.unit,
                          rate: it.rate,
                        }}
                        currentList={clientPriceList}
                        onAdded={(entry) => {
                          setClientPriceList(prev => [...prev, entry]);
                          // Reflect the new custom-price tag on this row
                          setForm(f => ({
                            ...f,
                            items: f.items.map((row, idx) => idx === i ? { ...row, has_custom_price: true } : row),
                          }));
                        }}
                      />
                    )}
                    {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)}><X size={12} className="text-zinc-600" /></button>}
                  </div>
                  );
                })}
                <button type="button" onClick={addItem} data-testid="add-item-btn" className="text-amber-400 text-[10px] flex items-center gap-1"><Plus size={10} /> Add Item</button>
              </div>
              <textarea rows={2} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Order notes..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none resize-none" />

              {/* Pipeline / Scheduling */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-zinc-500 text-xs mb-1 block">Production Line</label>
                  <select value={form.production_line || ''} onChange={e => setForm(f => ({ ...f, production_line: e.target.value }))}
                    data-testid="form-prod-line"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none">
                    <option value="">— Unassigned —</option>
                    {prodLines.filter(l => l.active !== false).map(l => (
                      <option key={l.id} value={l.name}>{l.name}{l.code ? ` (${l.code})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-500 text-xs mb-1 block">Priority</label>
                  <select value={form.priority || 'general'} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    data-testid="form-priority"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none">
                    <option value="high">High · Urgent</option>
                    <option value="general">General</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-500 text-xs mb-1 block">Production Due</label>
                  <input type="date" value={form.production_due_date || ''} onChange={e => setForm(f => ({ ...f, production_due_date: e.target.value }))}
                    data-testid="form-prod-due"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs mb-1 block">Dispatch Due</label>
                  <input type="date" value={form.dispatch_due_date || ''} onChange={e => setForm(f => ({ ...f, dispatch_due_date: e.target.value }))}
                    data-testid="form-dispatch-due"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-500 text-xs mb-1 block">Remark</label>
                  <input value={form.remark || ''} onChange={e => setForm(f => ({ ...f, remark: e.target.value }))}
                    data-testid="form-remark" placeholder="Internal remark for this order"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="submit-order-btn" type="submit" disabled={submitting || !form.client}
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </form>
        </MobileModal>
      )}

      {invoiceOrder && (
        <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)}
          onUpdate={(updated) => { setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o)); setInvoiceOrder(updated); notifySalesOrdersChanged(); }} />
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {showVoiceOrder && (
        <VoiceOrderModal
          onClose={() => setShowVoiceOrder(false)}
          onCreated={() => { setShowVoiceOrder(false); fetchData(); notifySalesOrdersChanged(); }}
        />
      )}
    </div>
  );
}
